import React, { useState, useEffect, SetStateAction } from 'react'
import Table from 'react-bootstrap/Table'
import Dropdown from 'react-bootstrap/Dropdown'
import FormControl from 'react-bootstrap/FormControl'
import Button from 'react-bootstrap/Button'
import { v4 as uuidv4 } from 'uuid'
import { MdCancel } from 'react-icons/md'
import { PhoneInput } from 'react-international-phone'

import { red } from '../../../styles/themeColors'
import { offeringTypes } from '../../../common/constants'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { FormatNumber, ValidationModal } from '../../../components'
import {
  addPaidGuest,
  editPaidGuest,
  paidGuestsWasUpdated,
  removePaidGuest,
} from '../../../features/bookingSlice'
import {
  OfferingAddOns,
  OfferingAddOnOptions,
  Booking,
  IBookingGuest,
  GuestShort,
} from '../../../types'
import { useValidatePhone } from '../../../components/SigninSignup/components/SigninSignupFunctions'

import { AddOnsSelectContainer } from './style'
import {
  getAddonFrequency,
  getPaidGuestOptions,
  getPaidGuestsLimit,
  paidGuestsGuideText,
} from './bookingFunctions'
import SelectGuestModal, { IAddGuestPayload } from './SelectGuestModal'

// Convertimos el tipo de la función setPhoneError para hacerlo compatible
type PhoneErrorSetter = (value: SetStateAction<string>) => void

export const PaidGuests: React.FC<{
  booking: Booking
  showPlusOne: boolean
  myGroup?: GuestShort[]
}> = ({ booking, showPlusOne, myGroup = [] }) => {
  const dispatch = useAppDispatch()
  const {
    availableAddons: { extraGuestAddon, extraKidAddon },
    paidGuests = [],
    freeGuests = [],
    paidGuestsUpdated,
  } = useAppSelector(state => state.booking)

  const [showSelectGuestModal, setShowSelectGuestModal] = useState(false)
  const [showValidationModal, setShowValidationModal] = useState(false)
  const [selectedOption, setSelectedOption] = useState<OfferingAddOnOptions>()
  const [btnHelper, setBtnHelper] = useState<Record<string, boolean>>({})
  const { eventComponent: ec } = booking
  const isRoom = ec.offering.__typename === offeringTypes.roomOffering

  // Phone validation states
  const [phones, setPhones] = useState<Record<string, string | undefined>>({})
  const [phoneErrors, setPhoneErrors] = useState<Record<string, string>>({})
  const { validatePhone } = useValidatePhone()

  const addons: { [s: string]: OfferingAddOns } = {}
  if (extraGuestAddon) addons[extraGuestAddon.id] = extraGuestAddon
  if (extraKidAddon) addons[extraKidAddon.id] = extraKidAddon

  const limit = getPaidGuestsLimit(ec)

  // Initialize phones state for existing paid guests
  useEffect(() => {
    if (paidGuests.length > 0) {
      const initialPhones: Record<string, string | undefined> = {}
      const initialBtnHelper: Record<string, boolean> = {}
      paidGuests.forEach(guest => {
        initialPhones[`phone-${guest.id}`] = guest.guest.phone || undefined
        initialBtnHelper[guest.id] = true
      })
      setPhones(initialPhones)
      setBtnHelper(initialBtnHelper)
    }
  }, [paidGuests])

  const handleOptionSelect = (currentOption: OfferingAddOnOptions) => {
    setSelectedOption(currentOption)
    setShowSelectGuestModal(true)
    dispatch(paidGuestsWasUpdated(true))
  }

  const handleAddGuest = (guestData: IAddGuestPayload) => {
    if (!selectedOption) return

    const type =
      selectedOption.offeringAddOnId === extraGuestAddon?.id ? 'adult' : 'kid'

    const guestId = guestData.guest?.id || uuidv4()
    // Create the paid guest object
    const paidGuest: IBookingGuest = {
      id: guestId,
      option: selectedOption,
      guest:
        guestData.guest ||
        ({
          firstName: guestData.firstName,
          lastName: guestData.lastName,
          email: guestData.email,
          phone: guestData.phone,
          isPlusOne: guestData.isPlusOne,
          id: uuidv4(),
          avatarUrl: null,
          flightInformation: null,
          group: null,
          plusOne: 0,
          plusOneCount: 0,
          role: null,
        } as GuestShort),
      type,
    }
    dispatch(addPaidGuest(paidGuest))

    // Initialize phone and button state for the new guest
    setPhones(prev => ({
      ...prev,
      [`phone-${guestId}`]: guestData.guest?.phone || guestData.phone,
    }))
    setBtnHelper(prev => ({ ...prev, [guestId]: true }))
    dispatch(paidGuestsWasUpdated(false))
    setSelectedOption(undefined)
  }

  const handleDelete = (addon: IBookingGuest) => {
    dispatch(removePaidGuest(addon))
    // Cleanup phone and button states
    setPhones(prev => {
      const newPhones = { ...prev }
      delete newPhones[`phone-${addon.id}`]
      return newPhones
    })
    setBtnHelper(prev => {
      const newBtnHelper = { ...prev }
      delete newBtnHelper[addon.id]
      return newBtnHelper
    })
  }

  // Función auxiliar para establecer errores de teléfono por ID de invitado
  const handleSetPhoneError = (guestId: string): PhoneErrorSetter => {
    return (value: SetStateAction<string>) => {
      setPhoneErrors(prevErrors => {
        let result = ''
        if (typeof value === 'function') {
          result = value(prevErrors[`phone-${guestId}`] || '')
        } else {
          result = value
        }

        return {
          ...prevErrors,
          [`phone-${guestId}`]: result,
        }
      })
    }
  }

  const handlePhoneChange = (value: string, guestId: string) => {
    if (value?.trim() === '+1') return // Ignore the default value

    const phoneValue = value.trim() === '' ? undefined : value
    const key = `phone-${guestId}`
    setPhones(prev => ({ ...prev, [key]: phoneValue }))

    validatePhone({
      phone: value,
      setPhoneError: handleSetPhoneError(guestId),
      required: false,
    })

    if (btnHelper[guestId] && phones[key] !== phoneValue) {
      setBtnHelper(prev => ({ ...prev, [guestId]: false }))
    }
    // Mark as updated only if value actually changed
    if (!paidGuestsUpdated && phones[key] !== phoneValue) {
      dispatch(paidGuestsWasUpdated(true))
    }
  }

  const dataHasChanged = (guestId: string) => {
    if (btnHelper[guestId]) {
      setBtnHelper(prev => ({ ...prev, [guestId]: false }))
    }

    if (!paidGuestsUpdated) {
      dispatch(paidGuestsWasUpdated(true))
    }
  }

  const handleSubmit = (
    guest: IBookingGuest,
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault()
    // Validate phone
    const phoneValue = phones[`phone-${guest.id}`]

    const phoneIsValid = validatePhone({
      phone: phoneValue,
      setPhoneError: handleSetPhoneError(guest.id),
      required: false,
    })

    // If phone is invalid, don't proceed with saving
    if (!phoneIsValid) {
      return
    }
    // Get form data
    const form = event.currentTarget
    const firstName = (form.elements.namedItem('firstName') as HTMLInputElement)
      ?.value
    const lastName = (form.elements.namedItem('lastName') as HTMLInputElement)
      ?.value
    const email = (form.elements.namedItem('email') as HTMLInputElement)?.value
    // Create updated guest object preserving the type and option
    const updatedGuest: IBookingGuest = {
      ...guest,
      guest: {
        ...guest.guest,
        firstName,
        lastName,
        email,
        phone: phoneValue || '',
      },
    }
    dispatch(editPaidGuest(updatedGuest))
    setBtnHelper(prev => ({ ...prev, [guest.id]: true }))
    dispatch(paidGuestsWasUpdated(false))
  }

  const combinedOptions = getPaidGuestOptions({
    extraGuestAddon,
    extraKidAddon,
    paidGuests,
  })

  // Filter out members that are already in free guests or paid guests
  const availableGroupMembers = myGroup.filter(
    member =>
      !freeGuests.some(fg => fg.id === member.id) &&
      !paidGuests.some(pg => pg.id === member.id)
  )

  return (
    <>
      <ValidationModal
        text="You have reached the maximum number of +1.<br />"
        textClass="fz-18 pt-2"
        show={showValidationModal}
        onHide={() => setShowValidationModal(false)}
      />
      <p className="fz-12 mb-2 tc-lgray">
        {paidGuestsGuideText({
          extraGuestAddon,
          extraKidAddon,
          component: ec,
          limit,
        })}
      </p>

      {paidGuests.map(guest => (
        <form
          onSubmit={event => handleSubmit(guest, event)}
          id={`form-${guest.id}`}
          className="d-contents"
          key={guest.id}
        >
          <Table
            borderless
            size="sm"
            data-cy="paid-users"
            className={paidGuests.length <= 0 ? 'd-none' : 'paid-guests-table'}
          >
            <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>{/** save button */}</th>
                <th>{/** delete button */}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <p className="d-block d-md-none mb-1 fwsb">First Name</p>
                  <FormControl
                    name="firstName"
                    defaultValue={guest.guest.firstName}
                    onChange={() => dataHasChanged(guest.id)}
                    required
                  />
                </td>
                <td>
                  <p className="d-block d-md-none mb-1 fwsb">Last Name</p>
                  <FormControl
                    name="lastName"
                    defaultValue={guest.guest.lastName}
                    onChange={() => dataHasChanged(guest.id)}
                    required
                  />
                </td>
                <td>
                  <p className="d-block d-md-none mb-1 fwsb">Email</p>
                  <FormControl
                    name="email"
                    defaultValue={guest.guest.email ?? ''}
                    onChange={() => dataHasChanged(guest.id)}
                    type="email"
                  />
                </td>
                <td>
                  <p className="d-block d-md-none mb-1 fwsb">Phone</p>
                  <PhoneInput
                    placeholder="Phone number"
                    defaultCountry="us"
                    value={phones[`phone-${guest.id}`]}
                    onChange={value => handlePhoneChange(value, guest.id)}
                    className="international-phone-small"
                    inputClassName="international-phone-input-small"
                    countrySelectorStyleProps={{
                      buttonClassName: 'international-phone-button-small',
                    }}
                  />
                  {phoneErrors[`phone-${guest.id}`] && (
                    <span className="fz-12 text-danger d-block">
                      {phoneErrors[`phone-${guest.id}`]}
                    </span>
                  )}
                </td>
                <td className="align-middle">
                  <Button
                    size="sm"
                    type="submit"
                    className="rounded-pill m-0 px-4 px-md-2"
                    disabled={btnHelper[guest.id]}
                  >
                    save
                  </Button>
                </td>
                <td className="align-middle" style={{ width: '30px' }}>
                  <MdCancel
                    size={20}
                    color={red}
                    title="Delete"
                    className="cursor"
                    style={{ margin: '0' }}
                    onClick={() => handleDelete(guest)}
                  />
                </td>
              </tr>
              <tr>
                <td colSpan={1}>
                  <span
                    className="fz-11 tc-lgray d-inline-block position-relative"
                    style={{ top: '-4px', left: '1px' }}
                  >
                    {guest.option.description}
                  </span>
                </td>
                {!btnHelper[guest.id] && (
                  <td className="text-danger fz-12 text-right pt-0" colSpan={5}>
                    * unsaved changes
                  </td>
                )}
              </tr>
            </tbody>
          </Table>
        </form>
      ))}

      {paidGuests.length < limit && (
        <AddOnsSelectContainer className="pt-2 pb-4" data-cy="addons-select">
          <Dropdown className="d-inline-block">
            <Dropdown.Toggle variant="outline-primary" id="add-addons-select">
              Add Guests
            </Dropdown.Toggle>

            <Dropdown.Menu>
              {combinedOptions.map(el => (
                <Dropdown.Item
                  onClick={() => handleOptionSelect(el)}
                  key={el.id}
                >
                  Add {el.label} +<FormatNumber n={el.price} />
                  {getAddonFrequency({
                    id: el.offeringAddOnId,
                    isRoom,
                    addons,
                  })}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </AddOnsSelectContainer>
      )}

      <SelectGuestModal
        show={showSelectGuestModal}
        onHide={() => {
          setShowSelectGuestModal(false)
          dispatch(paidGuestsWasUpdated(false))
        }}
        onAddGuest={handleAddGuest}
        myGroup={availableGroupMembers}
        showPlusOne={showPlusOne}
        currentGuest={booking.guest}
      />
    </>
  )
}
